import { For, Show, createSignal } from "solid-js";

import { SelectionActionBar } from "~/components/SelectionActionBar";
import { useCompleteTask, useRescheduleTask } from "~/lib/hooks";
import type { ActivityRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
  count: () => number;
  rows: () => ActivityRead[];
  onClear: () => void;
};

type Mode = "complete" | "snooze";

type RowOutcome = { id: string; subject: string; ok: boolean; message?: string };

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export function TasksBulkBar(props: Props) {
  const completeMutation = useCompleteTask(props.wsKey);
  const rescheduleMutation = useRescheduleTask(props.wsKey);

  const [mode, setMode] = createSignal<Mode | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [snoozeDays, setSnoozeDays] = createSignal(1);
  const [outcome, setOutcome] = createSignal<RowOutcome[] | null>(null);

  const reset = () => {
    setMode(null);
    setSnoozeDays(1);
    setOutcome(null);
  };
  const close = () => reset();

  const runComplete = async () => {
    setBusy(true);
    const results: RowOutcome[] = [];
    for (const t of props.rows()) {
      // Skip rows already completed — saves an API call and avoids the
      // version-mismatch noise an idempotent re-complete would produce.
      if (t.status === "completed") {
        results.push({ id: t.id, subject: t.subject, ok: true });
        continue;
      }
      try {
        await completeMutation.mutateAsync({
          activityId: t.id,
          version: t.version,
        });
        results.push({ id: t.id, subject: t.subject, ok: true });
      } catch (err) {
        results.push({
          id: t.id,
          subject: t.subject,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const runSnooze = async () => {
    setBusy(true);
    const days = Math.max(1, Math.floor(snoozeDays()));
    const results: RowOutcome[] = [];
    for (const t of props.rows()) {
      // Snooze relative to the current due_date when set, otherwise relative
      // to "now". Mirrors the single-row snooze affordance on TasksPage.
      const base = t.due_date ? new Date(t.due_date).getTime() : Date.now();
      const next = new Date(Math.max(base, Date.now()) + days * ONE_DAY_MS);
      try {
        await rescheduleMutation.mutateAsync({
          activityId: t.id,
          version: t.version,
          due_date: next.toISOString(),
        });
        results.push({ id: t.id, subject: t.subject, ok: true });
      } catch (err) {
        results.push({
          id: t.id,
          subject: t.subject,
          ok: false,
          message: (err as Error).message,
        });
      }
    }
    setOutcome(results);
    setBusy(false);
  };

  const okCount = () => outcome()?.filter((r) => r.ok).length ?? 0;
  const errCount = () => outcome()?.filter((r) => !r.ok).length ?? 0;

  return (
    <>
      <SelectionActionBar count={props.count} onClear={props.onClear}>
        <button type="button" class="btn btn-sm btn-success" onClick={() => setMode("complete")}>
          Complete
        </button>
        <button type="button" class="btn btn-sm" onClick={() => setMode("snooze")}>
          Snooze
        </button>
      </SelectionActionBar>

      <Show when={mode()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="tasks-bulk-title">
          <div class="modal-box max-w-xl">
            <h3 id="tasks-bulk-title" class="font-bold text-lg mb-3">
              <Show when={mode() === "complete"}>
                Complete {props.count()} task{props.count() === 1 ? "" : "s"}?
              </Show>
              <Show when={mode() === "snooze"}>
                Snooze {props.count()} task{props.count() === 1 ? "" : "s"}
              </Show>
            </h3>

            <Show when={!outcome()}>
              <Show when={mode() === "complete"}>
                <p class="text-sm opacity-80 mb-4">
                  Marks the selected tasks as completed. Tasks already complete are skipped.
                </p>
              </Show>
              <Show when={mode() === "snooze"}>
                <p class="text-sm opacity-80 mb-3">
                  Push each task's due date forward. If it's already past due, the new date is
                  measured from today instead of the original due date.
                </p>
                <label class="form-control max-w-xs">
                  <span class="label-text text-sm">Days forward</span>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    class="input input-bordered"
                    value={snoozeDays()}
                    onInput={(e) => setSnoozeDays(Math.max(1, Number(e.currentTarget.value || 1)))}
                    aria-label="Days to snooze"
                  />
                </label>
              </Show>
            </Show>

            <Show when={outcome()}>
              {(rows) => (
                <div class="mb-4">
                  <div class="stats stats-horizontal w-full text-sm shadow-sm">
                    <div class="stat py-2">
                      <div class="stat-title">Total</div>
                      <div class="stat-value text-lg">{rows().length}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Succeeded</div>
                      <div class="stat-value text-lg text-success">{okCount()}</div>
                    </div>
                    <div class="stat py-2">
                      <div class="stat-title">Failed</div>
                      <div class="stat-value text-lg text-error">{errCount()}</div>
                    </div>
                  </div>
                  <Show when={errCount() > 0}>
                    <div
                      class="mt-3 max-h-64 overflow-y-auto bg-base-200 rounded-lg p-2"
                      aria-label="Failed rows"
                    >
                      <ul class="text-xs space-y-1">
                        <For each={rows().filter((r) => !r.ok)}>
                          {(r) => (
                            <li>
                              <strong>{r.subject}</strong>: {r.message ?? "Failed"}
                            </li>
                          )}
                        </For>
                      </ul>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <div class="modal-action">
              <button type="button" class="btn btn-ghost" onClick={close} disabled={busy()}>
                {outcome() ? "Close" : "Cancel"}
              </button>
              <Show when={!outcome()}>
                <Show when={mode() === "complete"}>
                  <button
                    type="button"
                    class="btn btn-success"
                    onClick={runComplete}
                    disabled={busy()}
                  >
                    {busy() ? "Completing…" : "Complete"}
                  </button>
                </Show>
                <Show when={mode() === "snooze"}>
                  <button
                    type="button"
                    class="btn btn-primary"
                    onClick={runSnooze}
                    disabled={busy()}
                  >
                    {busy() ? "Snoozing…" : `Snooze ${snoozeDays()}d`}
                  </button>
                </Show>
              </Show>
            </div>
          </div>
        </dialog>
      </Show>
    </>
  );
}
